import type { LevelAssessmentModel } from "@shared/labs/level-assessment-model";
import { SEED_MODEL_ID } from "@shared/labs/level-assessment-model";
import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from "react";
import { rpcClient } from "@/lib/rpc";

/**
 * Active evaluation model — the one the user is currently working with.
 *
 * Persistence:
 *   - The selected model id lives in `localStorage` under `LS_KEY` (one
 *     key, browser-wide). PR 3 default: the shipped seed.
 *   - The full model JSON is loaded from the server via RPC the first time
 *     it's needed and cached in the context for the session. Refresh-aware:
 *     when the user picks a different model, the new one is loaded and
 *     becomes active.
 *
 * Why a context and not just localStorage:
 *   - Every result component already accepts a `modelSnapshot` from the
 *     candidate result, so live-rendering of assessment outputs never
 *     touches this context. But the form view + the config screens (model
 *     picker, signal-matrix, prompts-editor, calibration tab) need a
 *     SINGLE source of truth for "which model is the user looking at" — a
 *     context avoids ten copies of the same `MODEL_ID` constant + ten RPC
 *     refetches.
 *   - PR 4 builds the model-management screens. Those will mutate the
 *     model (create, edit, bootstrap, delete) and need to invalidate the
 *     cached value. Putting it behind a hook lets us add invalidation
 *     without touching every consumer.
 */

const LS_KEY = "labs.level-assessment.active-model-id";

function readStoredId(): string {
  if (typeof window === "undefined") return SEED_MODEL_ID;
  try {
    const v = window.localStorage.getItem(LS_KEY);
    if (typeof v === "string" && v.length > 0) return v;
  } catch {
    /* ignore corrupt storage — fall back to seed */
  }
  return SEED_MODEL_ID;
}

function writeStoredId(modelId: string): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(LS_KEY, modelId);
  } catch {
    /* storage quota / disabled — silent */
  }
}

export interface ActiveModelContextValue {
  /** Currently selected model id. Always a string — the context guarantees
   *  it resolves to a valid id (falls back to the seed on first run). */
  modelId: string;
  /** The loaded model JSON. `null` while the first fetch is in flight. */
  model: LevelAssessmentModel | null;
  /** The discipline the user has selected to score against — `null` for a
   *  cross-only run (model with no disciplines, or none picked yet). Auto-set
   *  to the model's first discipline when one loads. */
  disciplineId: string | null;
  /** Pick the discipline to score against. */
  setDisciplineId: (id: string | null) => void;
  /** Loading state for the initial fetch. After mount, `loading` is true
   *  until `model` resolves. Subsequent `setModelId` calls also flip this. */
  loading: boolean;
  /** Error from the most recent load, or null. */
  error: string | null;
  /** Switch to a different model. Persists to localStorage and re-fetches. */
  setModelId: (id: string) => void;
  /** Force-refresh the cached `model` (e.g. after the user edited it via
   *  the config screen). */
  refresh: () => Promise<void>;
}

const ActiveModelContext = createContext<ActiveModelContextValue | null>(null);

export function ActiveModelProvider({ children }: { children: ReactNode }) {
  const [modelId, setModelIdState] = useState<string>(() => readStoredId());
  const [model, setModel] = useState<LevelAssessmentModel | null>(null);
  const [disciplineId, setDisciplineId] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const res = (await rpcClient.labs.levelAssessmentModelGet({ modelId: id })) as {
        model: LevelAssessmentModel;
      };
      setModel(res.model);
      // Default the discipline to the model's first (lowest-order) one, or
      // null for a cross-only model.
      setDisciplineId(res.model.disciplines[0]?.id ?? null);
    } catch (e) {
      const message = e instanceof Error ? e.message : "Erro inesperado";
      setError(message);
      // Fall back to the seed if the saved id is invalid (model deleted,
      // typo, etc.). Avoid a hard error state — the user can still pick
      // another model from the dropdown.
      if (id !== SEED_MODEL_ID) {
        try {
          const seed = (await rpcClient.labs.levelAssessmentModelGet({
            modelId: SEED_MODEL_ID,
          })) as { model: LevelAssessmentModel };
          setModel(seed.model);
          setDisciplineId(seed.model.disciplines[0]?.id ?? null);
          setModelIdState(SEED_MODEL_ID);
          writeStoredId(SEED_MODEL_ID);
          setError(`Modelo "${id}" não encontrado — voltando para o padrão (${SEED_MODEL_ID}).`);
        } catch {
          /* fully broken — keep the error from the original load */
        }
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(modelId);
    // We only want this effect on mount + when modelId changes. `load` is
    // stable (wrapped in useCallback with empty deps).
  }, [modelId, load]);

  const setModelId = useCallback((id: string) => {
    writeStoredId(id);
    setModelIdState(id);
  }, []);

  const refresh = useCallback(async () => {
    await load(modelId);
  }, [load, modelId]);

  const value: ActiveModelContextValue = {
    modelId,
    model,
    disciplineId,
    setDisciplineId,
    loading,
    error,
    setModelId,
    refresh,
  };

  return <ActiveModelContext.Provider value={value}>{children}</ActiveModelContext.Provider>;
}

/** Read the active model context. Throws when used outside the provider —
 *  catches missing-provider bugs at render time instead of silently
 *  defaulting to the seed. */
export function useActiveModel(): ActiveModelContextValue {
  const ctx = useContext(ActiveModelContext);
  if (!ctx) {
    throw new Error(
      "useActiveModel must be used inside <ActiveModelProvider>. Wrap the level-assessment page.",
    );
  }
  return ctx;
}
